import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useAccessRequestSettingsQuery$data,
	useAccessRequestSettingsQuery$variables,
} from './__generated__/useAccessRequestSettingsQuery.graphql.ts';

/**
 * Reads access-request settings from `confluence_accessRequestSettings` — the narrow query exposing
 * only `enabled`/`messageContent` for spaces and content, callable by any authenticated user.
 *
 * Non-admin surfaces depend on that: `RestrictedSpaceScreen` and the share dialog read these values
 * for ordinary viewers. Don't restrict callers by role (e.g. `skip: !isSiteAdmin`), and don't move
 * back to `confluence.siteConfiguration` — it's admin-only (it also carries login/XSRF settings),
 * so it resolved to `null` for every non-admin viewer.
 */
export const ACCESS_REQUEST_SETTINGS_QUERY: GraphQLTaggedNode = graphql`
	query useAccessRequestSettingsQuery($cloudId: ID!) {
		confluence_accessRequestSettings(cloudId: $cloudId) {
			contentAccessRequestSettings {
				enabled
				messageContent
			}
			spaceAccessRequestSettings {
				enabled
				messageContent
			}
		}
	}
`;

export const useAccessRequestSettings = ({
	skip = false,
}: {
	/**
	 * Skips the query entirely. Callers outside the admin settings page use this to avoid issuing a
	 * request when the feature is gated off. Every returned value stays `undefined` while skipped.
	 */
	skip?: boolean;
} = {}): {
	spaceEnabled: boolean | null | undefined;
	contentEnabled: boolean | null | undefined;
	spaceMessageContent: string | null | undefined;
	contentMessageContent: string | null | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	refetch: () => void;
} => {
	const { cloudId } = useSessionData();

	const { data, loading, error, refetch } = useQueryInterop<
		useAccessRequestSettingsQuery$data,
		useAccessRequestSettingsQuery$variables
	>(ACCESS_REQUEST_SETTINGS_QUERY, {
		variables: { cloudId },
		skip,
	});

	const settings = data?.confluence_accessRequestSettings;

	const spaceEnabled = settings?.spaceAccessRequestSettings?.enabled;
	const contentEnabled = settings?.contentAccessRequestSettings?.enabled;
	const spaceMessageContent = settings?.spaceAccessRequestSettings?.messageContent;
	const contentMessageContent = settings?.contentAccessRequestSettings?.messageContent;

	return {
		spaceEnabled,
		contentEnabled,
		spaceMessageContent,
		contentMessageContent,
		loading,
		error,
		refetch,
	};
};
